import { Bell, KeyRound, Settings as SettingsIcon, Shield, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import TwoFactorSetup from './auth/TwoFactorSetup';

const Settings = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('security');
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestMsg, setDigestMsg] = useState('');

  const fetchPreferences = useCallback(async () => {
    try {
      const { data } = await api.get('/user/profile');
      if (data.success) {
        const prefs = data.data.user.preferences || {};
        setDigestEnabled(prefs.weekly_digest !== false);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  const toggleDigest = async (enabled) => {
    setSavingDigest(true);
    setDigestMsg('');
    try {
      await api.post('/user/preferences/weekly-digest', { enabled });
      setDigestEnabled(enabled);
      setDigestMsg(enabled ? 'Weekly digest enabled.' : 'Weekly digest disabled.');
    } catch {
      setDigestMsg('Failed to update preference.');
    } finally {
      setSavingDigest(false);
    }
  };

  const tabs = [
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'account', label: 'Account', icon: User },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <SettingsIcon size={24} className="text-slate-400" />
        <h1 className="text-2xl font-bold text-slate-200">Settings</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-slate-800/50 p-1 rounded-xl border border-slate-700/50">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              tab === id
                ? 'bg-slate-700 text-slate-200'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Security Tab */}
      {tab === 'security' && (
        <div className="space-y-4">
          <TwoFactorSetup />

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <KeyRound size={20} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-slate-200 font-semibold">Password</h3>
                <p className="text-xs text-slate-400">Change your account password</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-3">
              Use "Forgot password?" on the login screen to reset your password via email.
            </p>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {tab === 'notifications' && (
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Bell size={20} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-slate-200 font-semibold">Weekly Digest</h3>
                  <p className="text-xs text-slate-400">Sent every Sunday at 8 AM UTC</p>
                </div>
              </div>
              <button
                onClick={() => toggleDigest(!digestEnabled)}
                disabled={savingDigest}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  digestEnabled ? 'bg-emerald-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    digestEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-slate-400 ml-13">
              A summary of your weekly P&L, win rate, and top symbol — delivered to {user?.email}.
            </p>
            {digestMsg && (
              <p className="mt-2 text-xs text-emerald-400">{digestMsg}</p>
            )}
          </div>
        </div>
      )}

      {/* Account Tab */}
      {tab === 'account' && (
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <User size={20} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-slate-200 font-semibold">Profile</h3>
                <p className="text-xs text-slate-400">Your account information</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                <span className="text-sm text-slate-400">Name</span>
                <span className="text-sm text-slate-200">{user?.firstName} {user?.lastName}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                <span className="text-sm text-slate-400">Email</span>
                <span className="text-sm text-slate-200">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                <span className="text-sm text-slate-400">Email verified</span>
                <span className={`text-sm font-medium ${user?.isVerified ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {user?.isVerified ? 'Verified' : 'Not verified'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-400">Plan</span>
                <span className="text-sm text-slate-200 capitalize">
                  {user?.subscriptionTier || user?.subscription_tier || 'Free'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
